import type {
  BookChain,
  BookChainResult,
  CancelLaterResult,
  ChainProposalRequest,
  ChainProposalResult,
  ShiftChain,
  ShiftChainResult,
  TreatmentPlan,
  WaitingPart,
  StartCourse,
} from "@dentacore/shared-types";

import { api } from "./client";

/**
 * Treatment plans — STORY-076, STORY-077.
 *
 * **Branch-scoped requests, clinic-wide answers.** A plan belongs to a patient
 * and patients are shared across a clinic's branches (§4.2), so the header says
 * where the caller is standing and the server decides what that entitles them
 * to. Nothing here narrows a plan to one location — the crown prepped at one
 * branch is cemented at the other.
 *
 * **Every verb returns the whole plan**, not the piece it touched, so a screen
 * redraws from one answer and cannot hold an order the server disagrees with.
 */

/** Omit `patientId` for the clinic's recent plans — what the rail shows. */
export function listPlans(patientId?: string, signal?: AbortSignal): Promise<TreatmentPlan[]> {
  const query = patientId === undefined ? "" : `?patientId=${patientId}`;
  return api<TreatmentPlan[]>(`/plans${query}`, { signal, branchScoped: true });
}

export function getPlan(planId: string, signal?: AbortSignal): Promise<TreatmentPlan> {
  return api<TreatmentPlan>(`/plans/${planId}`, { signal, branchScoped: true });
}

/**
 * Start a patient's course of a multi-visit procedure (PV3) — the hidden
 * record the booking form then books visit 1 into and chain-books the rest of.
 */
export function startCourse(body: StartCourse): Promise<TreatmentPlan> {
  return api<TreatmentPlan>("/plans/from-procedure", {
    method: "POST",
    body,
    branchScoped: true,
  });
}

export function proposeChain(
  body: ChainProposalRequest,
  signal?: AbortSignal,
): Promise<ChainProposalResult> {
  return api<ChainProposalResult>("/plans/chain-proposal", {
    method: "POST",
    body,
    branchScoped: true,
    signal,
  });
}

/**
 * Book several visits of a plan at once. **The reply has to be read** (CH10):
 * a 201 can carry refused visits, which stay unbooked and wait to be booked.
 */
export function bookChain(planId: string, body: BookChain): Promise<BookChainResult> {
  return api<BookChainResult>(`/plans/${planId}/book-chain`, {
    method: "POST",
    body,
    branchScoped: true,
  });
}

/**
 * Move one visit and shift the later ones with it (CH4) — what *Yes* means when
 * a move offers it. **Read `refused`**: a later visit that could not move stays
 * where it was.
 */
export function shiftChain(planId: string, body: ShiftChain): Promise<ShiftChainResult> {
  return api<ShiftChainResult>(`/plans/${planId}/shift-chain`, {
    method: "POST",
    body,
    branchScoped: true,
  });
}

/**
 * Cancel every later booked visit of a plan (CH11, CH13) — *Cancel them too*.
 * The chosen visit's own status is changed first, by the ordinary route.
 */
export function cancelLater(planId: string, appointmentId: string): Promise<CancelLaterResult> {
  return api<CancelLaterResult>(`/plans/${planId}/cancel-later`, {
    method: "POST",
    body: { appointmentId },
    branchScoped: true,
  });
}

/**
 * Attach a visit to an appointment that already exists (decision D6).
 *
 * The other direction from `bookPart`, which creates one. This is what lets a
 * plan be reached from the calendar, where most booking actually happens — and
 * it re-runs no turnover or clash rule, because it moves nothing.
 */
export function linkPart(
  planId: string,
  partId: string,
  appointmentId: string,
): Promise<TreatmentPlan> {
  return api<TreatmentPlan>(`/plans/${planId}/parts/${partId}/link`, {
    method: "POST",
    body: { appointmentId },
    branchScoped: true,
  });
}

/**
 * Correct a step's tooth or price (PP-D5). A completed step needs `reason`, and
 * is refused once its invoice is issued.
 */
export function correctStep(
  planId: string,
  partId: string,
  itemId: string,
  body: { fdiNumber?: number; priceCents?: number; overrideNote?: string; reason?: string },
): Promise<TreatmentPlan> {
  return api<TreatmentPlan>(`/plans/${planId}/parts/${partId}/items/${itemId}`, {
    method: "PATCH",
    body,
    branchScoped: true,
  });
}

/** Undo a step marked done by mistake, while its invoice is a draft (PP-D6). */
export function uncompleteStep(
  planId: string,
  partId: string,
  itemId: string,
  reason: string,
): Promise<TreatmentPlan> {
  return api<TreatmentPlan>(`/plans/${planId}/parts/${partId}/items/${itemId}/uncomplete`, {
    method: "POST",
    body: { reason },
    branchScoped: true,
  });
}

/**
 * Mark every outstanding step of one visit done — and, behind it, any later
 * visit already completed on the calendar, in order (PP-D9).
 */
export function completeVisit(planId: string, partId: string): Promise<TreatmentPlan> {
  return api<TreatmentPlan>(`/plans/${planId}/parts/${partId}/complete`, {
    method: "POST",
    body: {},
    branchScoped: true,
  });
}

/** Detach a visit from its appointment. Neither is deleted. */
export function unlinkPart(planId: string, partId: string): Promise<TreatmentPlan> {
  return api<TreatmentPlan>(`/plans/${planId}/parts/${partId}/link`, {
    method: "DELETE",
    branchScoped: true,
  });
}

/**
 * Every visit waiting for a date — STORY-082a.
 *
 * **Not polish**: a prompt somebody dismissed otherwise leaves a prepped crown
 * with no cementation date and nothing anywhere saying so.
 */
export function listWaiting(signal?: AbortSignal): Promise<WaitingPart[]> {
  return api<WaitingPart[]>("/plans/waiting", { signal, branchScoped: true });
}
